// Phase 44 — Customer Import History viewer. Manager-only dialog listing
// every prior commit to `/api/customers/import/commit`, with a row click
// to drill into the full audit-trail entry (errors_summary + audit_log).
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, FileText, AlertTriangle, RefreshCcw } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IE", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function CustomerImportHistory({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // selected history entry id (drill-down)
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers/import-history", { params: { limit: 50 } });
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load import history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) { setActive(null); setDetail(null); return; }
    load();
  }, [open]);

  useEffect(() => {
    if (!active) { setDetail(null); return; }
    (async () => {
      setDetailLoading(true);
      try {
        const { data } = await api.get(`/customers/import-history/${active}`);
        setDetail(data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load entry");
        setActive(null);
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [active]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[92vh] overflow-y-auto"
        data-testid="customer-import-history-dialog"
      >
        <DialogHeader>
          <DialogTitle>Customer import history</DialogTitle>
          <DialogDescription>
            Every prior bulk import recorded for this account. Click a row for full details.
          </DialogDescription>
        </DialogHeader>

        {!active ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[11px] mono uppercase tracking-wider text-muted-foreground">
                {loading ? "Loading…" : `${items.length} entr${items.length === 1 ? "y" : "ies"}`}
              </div>
              <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="import-history-refresh">
                <RefreshCcw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
            {!loading && items.length === 0 ? (
              <div className="bg-card border border-dashed border-border rounded-[8px] p-10 text-center text-sm text-muted-foreground">
                No prior imports recorded yet.
              </div>
            ) : (
              <div className="border border-border rounded-[8px] overflow-x-auto">
                <table className="w-full text-sm" data-testid="import-history-table">
                  <thead className="bg-secondary/40">
                    <tr>
                      <th className="text-left px-3 py-2">When</th>
                      <th className="text-left px-3 py-2">File</th>
                      <th className="text-left px-3 py-2">Strategy</th>
                      <th className="text-right px-3 py-2">Total</th>
                      <th className="text-right px-3 py-2">Created</th>
                      <th className="text-right px-3 py-2">Updated</th>
                      <th className="text-right px-3 py-2">Skipped</th>
                      <th className="text-right px-3 py-2">Invalid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((h) => (
                      <tr
                        key={h.id}
                        onClick={() => setActive(h.id)}
                        className="border-t border-border cursor-pointer hover:bg-secondary/30"
                        data-testid={`import-history-row-${h.id}`}
                      >
                        <td className="px-3 py-2 mono text-[12px]">{fmtDate(h.imported_at)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5 truncate max-w-[220px]">
                            <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="truncate" title={h.file_name}>{h.file_name || "(unnamed)"}</span>
                            <span className="text-[10px] mono text-muted-foreground uppercase shrink-0">
                              {h.file_format}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 mono text-[11px] uppercase">{h.duplicate_strategy}</td>
                        <td className="px-3 py-2 text-right mono">{h.rows_total}</td>
                        <td className="px-3 py-2 text-right mono text-emerald-700">{h.rows_created}</td>
                        <td className="px-3 py-2 text-right mono text-amber-700">{h.rows_updated}</td>
                        <td className="px-3 py-2 text-right mono text-muted-foreground">{h.rows_skipped}</td>
                        <td className="px-3 py-2 text-right mono">
                          {h.rows_invalid > 0 ? (
                            <span className="text-red-700 inline-flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" /> {h.rows_invalid}
                            </span>
                          ) : "0"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="import-history-detail">
            <Button variant="ghost" size="sm" onClick={() => setActive(null)} data-testid="import-history-back">
              <ChevronLeft className="h-3 w-3 mr-1" /> Back to list
            </Button>
            {detailLoading || !detail ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="When" value={fmtDate(detail.imported_at)} mono />
                  <Stat label="File" value={detail.file_name || "—"} mono />
                  <Stat label="Format" value={(detail.file_format || "").toUpperCase()} />
                  <Stat label="Strategy" value={detail.duplicate_strategy} />
                  <Stat label="Total rows" value={detail.rows_total} />
                  <Stat label="Created" value={detail.rows_created} cls="text-emerald-700" />
                  <Stat label="Updated" value={detail.rows_updated} cls="text-amber-700" />
                  <Stat label="Skipped" value={detail.rows_skipped} cls="text-muted-foreground" />
                  <Stat label="Invalid" value={detail.rows_invalid} cls={detail.rows_invalid > 0 ? "text-red-700" : ""} />
                  <Stat label="Duration" value={`${detail.duration_ms} ms`} mono />
                </div>
                {detail.errors_summary && (
                  <div className="bg-red-50 border border-red-200 rounded-[6px] p-3" data-testid="import-history-errors">
                    <div className="text-[11px] mono uppercase tracking-wider text-red-700 mb-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Errors summary
                    </div>
                    <pre className="text-[12px] font-mono text-red-900 whitespace-pre-wrap">{detail.errors_summary}</pre>
                  </div>
                )}
                {Array.isArray(detail.audit_log) && detail.audit_log.length > 0 && (
                  <div className="border border-border rounded-[6px] p-3">
                    <div className="text-[11px] mono uppercase tracking-wider text-muted-foreground mb-2">Audit log</div>
                    <div className="space-y-1 text-[12px] font-mono">
                      {detail.audit_log.map((a, i) => (
                        <div key={i} className="flex gap-3">
                          <span className="text-muted-foreground">{fmtDate(a.at)}</span>
                          <span className="font-bold">{a.action}</span>
                          <span className="text-muted-foreground truncate">{JSON.stringify(a.payload || {})}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, mono = false, cls = "" }) {
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${mono ? "mono" : ""} ${cls}`}>{value}</div>
    </div>
  );
}
